import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';

export interface CharacterModel {
  model: THREE.Group;
  mixer: THREE.AnimationMixer;
  clips: {
    idle: THREE.AnimationClip;
    attack: {
      head: THREE.AnimationClip;
      body: THREE.AnimationClip;
      legs: THREE.AnimationClip;
    };
    block: THREE.AnimationClip;
    hit: THREE.AnimationClip;
    dodge: THREE.AnimationClip;
    death: THREE.AnimationClip;
    victory: THREE.AnimationClip;
    ability: THREE.AnimationClip;
  };
}

export class ModelManager {
  private loader: FBXLoader;
  private textureLoader: THREE.TextureLoader;
  private modelCache: Map<string, CharacterModel> = new Map();

  constructor() {
    this.loader = new FBXLoader();
    this.textureLoader = new THREE.TextureLoader();
  }

  async loadCharacterModel(
    characterId: string,
    modelPath: string,
    texturePaths: { diffuse?: string; weapon?: string },
    animationMapping?: any,
    baseUrl?: string
  ): Promise<CharacterModel> {
    // Check cache first
    const cacheKey = `${characterId}_${modelPath}`;
    if (this.modelCache.has(cacheKey)) {
      return this.modelCache.get(cacheKey)!;
    }

    try {
      console.log(`[ModelManager] Loading character model for ${characterId} from ${modelPath}`);
      // Load the main model
      const model = await this.loadFBX(modelPath);
      
      // Apply textures but ignore errors
      if (texturePaths.diffuse) {
        try {
          const texture = await this.loadTexture(texturePaths.diffuse);
          this.applyTextureToModel(model, texture);
          console.log(`[ModelManager] Applied diffuse texture for ${characterId}`);
        } catch (textureError) {
          console.warn(`[ModelManager] Could not load diffuse texture for ${characterId}. Ignoring.`, textureError);
        }
      }

      // Enable shadows and double-sided materials
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          
          // Делаем материалы двусторонними
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                mat.side = THREE.DoubleSide;
              });
            } else {
              child.material.side = THREE.DoubleSide;
            }
          }
        }
      });

      // Create animation mixer
      const mixer = new THREE.AnimationMixer(model);

      // Load animations
      const clips = await this.loadAnimations(animationMapping, baseUrl);

      const characterModel: CharacterModel = {
        model,
        mixer,
        clips
      };

      // Cache the model
      this.modelCache.set(cacheKey, characterModel);

      return characterModel;
    } catch (error) {
      console.error(`Failed to load character model for ${characterId}:`, error);
      throw error;
    }
  }

  private loadFBX(path: string): Promise<THREE.Group> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        path,
        (object) => resolve(object),
        (progress) => {
          const percent = (progress.loaded / progress.total) * 100;
          console.log(`Loading model: ${percent.toFixed(2)}%`);
        },
        (error) => reject(error)
      );
    });
  }

  private loadTexture(path: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        path,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          resolve(texture);
        },
        undefined,
        (error) => reject(error)
      );
    });
  }

  private applyTextureToModel(model: THREE.Group, texture: THREE.Texture) {
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.material instanceof THREE.Material) {
          const material = child.material as THREE.MeshStandardMaterial;
          material.map = texture;
          material.needsUpdate = true;
        } else if (Array.isArray(child.material)) {
          child.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.map = texture;
              mat.needsUpdate = true;
            }
          });
        }
      }
    });
  }

  private async loadAnimations(animationMapping?: any, baseUrl?: string): Promise<CharacterModel['clips']> {
    // Create a dummy clip for fallback
    const createDummyClip = (name: string, duration: number = 1) => {
      const times = [0, duration];
      const values = [0, 0, 0, 0, 0, 0];
      const positionKF = new THREE.VectorKeyframeTrack('.position', times, values);
      return new THREE.AnimationClip(name, duration, [positionKF]);
    };

    // If no animation mapping provided, return dummy clips
    if (!animationMapping || !baseUrl) {
      return {
        idle: createDummyClip('idle', 2),
        attack: {
          head: createDummyClip('attack_head', 1),
          body: createDummyClip('attack_body', 1),
          legs: createDummyClip('attack_legs', 1)
        },
        block: createDummyClip('block', 0.5),
        hit: createDummyClip('hit', 0.5),
        dodge: createDummyClip('dodge', 1),
        death: createDummyClip('death', 2),
        victory: createDummyClip('victory', 3),
        ability: createDummyClip('ability', 1.5)
      };
    }

    // Load real animations from URLs
    const loadAnimationClip = async (animationFile: string): Promise<THREE.AnimationClip> => {
      if (!animationFile) {
        return createDummyClip('default', 1);
      }

      try {
        const animUrl = baseUrl + animationFile;
        console.log(`[ModelManager] Loading animation: ${animationFile}`);
        const fbx = await this.loadFBX(animUrl);
        
        if (fbx.animations && fbx.animations.length > 0) {
          console.log(`[ModelManager] Successfully loaded animation: ${animationFile}`);
          return fbx.animations[0];
        }
        
        console.warn(`[ModelManager] FBX for animation ${animationFile} loaded but contains no animation data.`);
        return createDummyClip('default', 1);
      } catch (error) {
        console.error(`[ModelManager] CRITICAL: Failed to load animation ${animationFile}.`, error);
        throw error; // Re-throw the error to fail the Promise.all
      }
    };

    // Load all animations
    const [idle, attackHead, attackBody, attackLegs, block, hit, dodge, death, victory, ability] = await Promise.all([
      loadAnimationClip(animationMapping.idle),
      loadAnimationClip(animationMapping.attackHead),
      loadAnimationClip(animationMapping.attackBody),
      loadAnimationClip(animationMapping.attackLegs),
      loadAnimationClip(animationMapping.block),
      loadAnimationClip(animationMapping.hit),
      loadAnimationClip(animationMapping.dodge),
      loadAnimationClip(animationMapping.death),
      loadAnimationClip(animationMapping.victory),
      loadAnimationClip(animationMapping.ability)
    ]);

    return {
      idle,
      attack: {
        head: attackHead,
        body: attackBody,
        legs: attackLegs
      },
      block,
      hit,
      dodge,
      death,
      victory,
      ability
    };
  }

  dispose() {
    // Clean up cached models
    this.modelCache.forEach((model) => {
      model.mixer.stopAllAction();
      model.model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          } else if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose());
          }
        }
      });
    });
    this.modelCache.clear();
  }
}
